import React from 'react';
import { FaFacebook, FaInstagram, FaWhatsapp, FaPhoneAlt, FaMapMarkerAlt, FaTiktok, FaSnapchatGhost } from 'react-icons/fa';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const Contact = () => {
  const { t, i18n } = useTranslation();
  const isArabic = i18n.language === 'ar';

  return (
    <section className="py-16 md:py-24 bg-[#f8fafc] overflow-hidden">
      <div className="container mx-auto max-w-6xl px-4">
        
        {/* Header Section */}
        <div className={`mb-12 ${isArabic ? 'text-right' : 'text-left'}`}>
          <h2 className="text-3xl md:text-4xl font-extrabold text-primeColor mb-4">
            {t('home.other.contact.socialMediaHeader')}
          </h2>
          <div className={`h-1.5 w-20 bg-secondColor rounded-full ${isArabic ? 'ml-auto' : 'mr-auto'}`}></div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-stretch">
          
          {/* Left Side: Contact Methods (7 Columns) */}
          <div className="lg:col-span-7 space-y-8">
            
            {/* Social Icons Row */}
            <div className={`flex flex-wrap gap-4 ${isArabic ? 'flex-row-reverse' : 'flex-row'}`}>
              {[
                { icon: <FaInstagram />, href: "https://www.instagram.com/laboratory.cmp/", color: "hover:bg-pink-500", text: "Instagram" },
                { icon: <FaFacebook />, href: "https://www.facebook.com/profile.php?id=61587114271681", color: "hover:bg-blue-600", text: "Facebook" },
                { icon: <FaTiktok />, href: "https://www.tiktok.com/@cmp_laboratory?_r=1&_t=ZS-93KkysfFcCm", color: "hover:bg-blue-600", text: "Tiktok" },
                { icon: <FaSnapchatGhost />, href: "https://www.snapchat.com/add/cmplab?share_id=2QxqGlMRjTY&locale=en-GB", color: "hover:bg-blue-600", text: "Snapchat" },
                { icon: <FaWhatsapp />, href: "https://api.whatsapp.com/send/?phone=971501344319", color: "hover:bg-green-500", text: "WhatsApp" }
              ].map((social, index) => (
                <a
                  key={index}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`flex items-center  text-primeColor-500 justify-center w-14 h-14 rounded-2xl bg-white shadow-sm border border-gray-100 text-2xl transition-all duration-300 hover:text-secondColor ${social.color} hover:-translate-y-2`}
                >
                  {social.icon}
                </a>
              ))}
            </div>

            {/* Phone Action Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {[
                { number: "+971 56 997 2730", href: "tel:+971569972730" },
                { number: "+971 50 134 4319", href: "tel:+971501344319" }
              ].map((phone, index) => (
                <a
                  key={index}
                  href={phone.href}
                  className="group flex items-center p-4 bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-md transition-all duration-300 no-underline"
                >
                  <div className="w-12 h-12 rounded-xl bg-secondColor/10 flex items-center justify-center text-secondColor group-hover:bg-secondColor group-hover:text-white transition-colors duration-300">
                    <FaPhoneAlt />
                  </div>
                  <div className={`${isArabic ? 'mr-4 text-right' : 'ml-4 text-left'}`}>
                    <p className="text-xs text-gray-500 uppercase tracking-wider font-bold">{t('home.other.contact.contactUs')}</p>
                    <p className="text-lg font-bold text-primeColor-500" dir="ltr">{phone.number}</p>
                  </div>
                </a>
              ))}
            </div>

            {/* Decorative Divider Replacement */}
            <div className="relative py-4 flex items-center">
                <div className="flex-grow border-t border-gray-200"></div>
                <span className="flex-shrink mx-4 text-gray-400 font-medium italic">
                    {t('home.other.contact.divider')}
                </span>
                <div className="flex-grow border-t border-gray-200"></div>
            </div>
          </div>

          {/* Right Side: Visual Location Card (5 Columns) */}
          <div className="lg:col-span-5">
            <div className="relative h-full min-h-[400px] rounded-3xl overflow-hidden shadow-2xl group">
              {/* The Image */}
              <img 
                src="https://res.cloudinary.com/dgcfs0gyw/image/upload/v1767476914/AF1QipO4EV9Fc-THt0xM08gYcL2w_tEiB1k6kX6P5TZv_s1360-w1360-h1020-rw_bkzzk6.webp" 
                alt="Our location" 
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" 
              />
              
              {/* Gradient Overlay */}
              <div className="absolute inset-0 bg-gradient-to-t from-primeColor/90 via-primeColor/20 to-transparent"></div>

              {/* Content on Image */}
              <div className="absolute bottom-0 left-0 right-0 p-8 flex flex-col items-center text-center">
              <a
  href="https://maps.app.goo.gl/Jetzk67KLinc291M7"
  target="_blank"
  rel="noopener noreferrer"
  className="group flex flex-col no-underline  items-center"
>
  <div className="mb-4 bg-white/20 backdrop-blur-md p-3 rounded-full text-white text-xl
                  transition-transform duration-300 group-hover:scale-110 ">
    <FaMapMarkerAlt  className='hover:color-primeColor-500'/>
  </div>

  <h3 className="text-white text-xl font-bold mb-6
                 transition-colors duration-300 group-hover:text-primeColor-300">
    {t('home.other.contact.locationTitle')}
  </h3>
</a>

                <Link 
                  to="/contact" 
                  className="w-full py-4 px-8 bg-primeColor-500 hover:bg-secondColor hover:text-primeColor text-white rounded-xl font-bold text-lg transition-all duration-300 shadow-lg transform active:scale-95 no-underline"
                >
                  {t('home.other.contact.button')}
                </Link>
              </div>
            </div>
          </div>

        </div>
      </div>
    </section>
  );
};

export default Contact;